import { cn } from "@/lib/utils";
import type { Comment } from "@cohesive/api-entities";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle } from "lucide-react";
import { Skeleton } from "../../ui/skeleton";
import {
  BaseEntityCard,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";
import { ContactCardAvatar } from "../ContactCard";

export type CommentCardProps = Comment &
  BaseEntityCardProps & {
    thread?: Comment[];
  };

export function CommentCardAvatar({
  className,
  style,
  author,
}: Partial<CommentCardProps>) {
  if (author) {
    return (
      <ContactCardAvatar {...author} className={className} style={style} />
    );
  }

  return (
    <BaseEntityCardIcon className={className} style={style}>
      <MessageCircle className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function CommentCard({
  platform,
  author,
  text,
  created_at,
  thread,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: CommentCardProps) {
  const authorName =
    typeof author === "object" && author && "name" in author
      ? (author as any).name
      : "Unknown Author";

  return (
    <>
      <BaseEntityCard
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMentionClick={onMentionClick}
        processes={processes}
        className={cn(className, {
          "rounded-br-none": thread && thread.length > 0,
        })}
        style={style}
      >
        <CommentCardAvatar author={author} />

        <BaseEntityCardContent>
          <BaseEntityCardHeader>
            <BaseEntityCardTitle>{authorName}</BaseEntityCardTitle>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {created_at &&
                  formatDistanceToNow(new Date(created_at), {
                    addSuffix: true,
                  })}
              </span>
              {actions && actions.length > 0 && (
                <BaseEntityCardContextMenu actions={actions} />
              )}
            </div>
          </BaseEntityCardHeader>
          <BaseEntityCardDescription className="truncate">
            {text}
          </BaseEntityCardDescription>
        </BaseEntityCardContent>
      </BaseEntityCard>
      {thread && thread.length > 0 && (
        <div className="ml-4">
          {thread.map((t, i) => (
            <CommentCard
              key={t.id}
              {...t}
              platform={platform}
              style={style}
              className={cn(
                className,
                "rounded-tl-none rounded-tr-none border-t-0 [&>div]:py-1.5",
                {
                  "rounded-br-none rounded-bl-none": i < thread.length - 1,
                },
              )}
              onClick={onClick}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function CommentCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[74px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-lg" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-3 w-1/2 mb-1.5" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
